import { useEffect, useRef, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@services/firebase';
import { Upload, X, Image } from 'lucide-react';
import toast from 'react-hot-toast';

const DEFAULT = {
  badge:           '🚀 Trusted by Forward-Thinking Businesses',
  headline:        'Turn Your Vision Into a Powerful Digital Reality',
  subtitle:        'Custom web solutions that drive results. From sleek static sites to AI-powered platforms—we build what your business needs to thrive online.',
  ctaPrimaryLabel: 'Work with us',
  ctaPrimaryLink:  '/register',
  ctaSecondaryLabel: 'See Our Work',
  ctaSecondaryLink:  '#projects',
  bgImageUrl:      '',
  bgOverlay:       '70',   // overlay opacity 0–100 when bgImage is set
};

export default function AdminHero() {
  const [form, setForm]           = useState(DEFAULT);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    getDoc(doc(db, 'site_settings', 'hero'))
      .then(snap => { if (snap.exists()) setForm({ ...DEFAULT, ...snap.data() }); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const storRef = ref(storage, `hero-media/${Date.now()}_${file.name}`);
    const task    = uploadBytesResumable(storRef, file);
    task.on(
      'state_changed',
      snap => setUploadPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      err  => { toast.error('Upload failed: ' + err.code); setUploadPct(null); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        set('bgImageUrl', url);
        setUploadPct(null);
        // Auto-save the new image URL to Firestore so the homepage updates immediately
        try {
          await setDoc(
            doc(db, 'site_settings', 'hero'),
            { ...form, bgImageUrl: url, updatedAt: serverTimestamp() },
            { merge: true },
          );
          toast.success('Image uploaded & saved');
        } catch (err) {
          toast.error('Image uploaded but save failed: ' + err.message);
        }
      },
    );
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await setDoc(doc(db, 'site_settings', 'hero'), {
        ...form,
        updatedAt: serverTimestamp(),
      });
      toast.success('Hero section saved!');
    } catch (err) {
      toast.error('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="h-64 flex items-center justify-center text-gray-400">Loading…</div>
  );

  // Live preview colours
  const previewStyle = form.bgImageUrl
    ? { backgroundImage: `linear-gradient(rgba(30,58,138,${form.bgOverlay / 100}),rgba(30,58,138,${form.bgOverlay / 100})),url(${form.bgImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : {};

  return (
    <>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Hero Section</h1>
      <p className="text-sm text-gray-500 mb-6">Edit the homepage banner. Changes go live immediately after saving.</p>

      {/* Live preview */}
      <div
        className="rounded-2xl bg-primary-navy text-white p-8 mb-8 text-center relative overflow-hidden"
        style={previewStyle}
      >
        {form.badge && (
          <div className="inline-block px-4 py-1.5 bg-white/10 border border-teal-400 rounded-full mb-4 text-sm font-semibold text-teal-300">
            {form.badge}
          </div>
        )}
        <h1 className="text-2xl font-bold mb-3 leading-tight">{form.headline || 'Your Headline'}</h1>
        <p className="text-white/80 mb-4 max-w-xl mx-auto text-sm">{form.subtitle}</p>
        <div className="flex gap-3 justify-center flex-wrap">
          {form.ctaPrimaryLabel && (
            <span className="px-4 py-2 bg-teal-500 rounded-lg text-sm font-semibold">{form.ctaPrimaryLabel}</span>
          )}
          {form.ctaSecondaryLabel && (
            <span className="px-4 py-2 border border-white/60 rounded-lg text-sm font-semibold">{form.ctaSecondaryLabel}</span>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">

        {/* Background image */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2"><Image className="w-4 h-4" /> Background Image</h2>

          {form.bgImageUrl && (
            <div className="relative inline-block">
              <img src={form.bgImageUrl} alt="hero bg" className="w-full max-h-40 object-cover rounded-lg border" />
              <button type="button" onClick={() => set('bgImageUrl', '')}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          <input type="file" ref={fileRef} accept="image/*" onChange={handleFilePick} className="hidden" />
          <button type="button" onClick={() => fileRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-teal-400 hover:text-teal-600 transition-colors">
            <Upload className="w-4 h-4" />
            Upload Background Image
          </button>

          {uploadPct !== null && (
            <div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-teal-500 h-2 rounded-full transition-all" style={{ width: `${uploadPct}%` }} />
              </div>
              <p className="text-xs text-gray-500 mt-1">{uploadPct}% uploaded</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Or paste image URL</label>
            <input type="url" value={form.bgImageUrl} onChange={e => set('bgImageUrl', e.target.value)}
              placeholder="https://…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm" />
          </div>

          {form.bgImageUrl && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dark overlay opacity: <span className="font-bold">{form.bgOverlay}%</span>
              </label>
              <input type="range" min="0" max="100" value={form.bgOverlay}
                onChange={e => set('bgOverlay', e.target.value)}
                className="w-full" />
            </div>
          )}
        </div>

        {/* Text content */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="font-semibold text-gray-800">Text Content</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Badge / Tag line</label>
            <input type="text" value={form.badge} onChange={e => set('badge', e.target.value)}
              placeholder="🚀 Trusted by Forward-Thinking Businesses"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Headline <span className="text-red-500">*</span></label>
            <textarea rows={2} required value={form.headline} onChange={e => set('headline', e.target.value)}
              placeholder="Turn Your Vision Into a Powerful Digital Reality"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle / Subheading</label>
            <textarea rows={3} value={form.subtitle} onChange={e => set('subtitle', e.target.value)}
              placeholder="Custom web solutions that drive results…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
          </div>
        </div>

        {/* CTAs */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="font-semibold text-gray-800">Call-to-Action Buttons</h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Label</label>
              <input type="text" value={form.ctaPrimaryLabel} onChange={e => set('ctaPrimaryLabel', e.target.value)}
                placeholder="Work with us"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Link</label>
              <input type="text" value={form.ctaPrimaryLink} onChange={e => set('ctaPrimaryLink', e.target.value)}
                placeholder="/register"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Label</label>
              <input type="text" value={form.ctaSecondaryLabel} onChange={e => set('ctaSecondaryLabel', e.target.value)}
                placeholder="See Our Work"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Link</label>
              <input type="text" value={form.ctaSecondaryLink} onChange={e => set('ctaSecondaryLink', e.target.value)}
                placeholder="#projects"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>
        </div>

        <div className="pb-8">
          <button type="submit" disabled={saving}
            className="px-6 py-2.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors">
            {saving ? 'Saving…' : 'Save Hero Section'}
          </button>
        </div>
      </form>
    </>
  );
}
